import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const schema = z.object({
  firstName: z.string().trim().min(1).max(100),
  lastName: z.string().trim().min(1).max(100),
  email: z.string().trim().email().max(255),
  phone: z.string().trim().min(5).max(30),
  companyName: z.string().trim().min(1).max(200),
  helpWith: z.array(z.string().max(100)).max(20).default([]),
  notes: z.string().trim().max(2000).optional().default(""),
  captchaToken: z.string().min(10).max(4000),
  // Honeypot — must be empty. Bots auto-fill all inputs.
  website: z.string().max(0).optional().default(""),
  // Client timestamp (ms) when the form mounted. Used to reject submissions
  // that happen suspiciously fast (typical bot behavior).
  formLoadedAt: z.number().int().positive(),
});

// Bot-detection heuristics
const MIN_FILL_TIME_MS = 3000; // humans rarely fill a contact form in <3s
const RECAPTCHA_MIN_SCORE = 0.7; // stricter than default 0.5
const EXPECTED_ACTION = "contact_submit";

async function verifyRecaptcha(token: string): Promise<{ ok: boolean; reason?: string }> {
  const secret = process.env.RECAPTCHA_SECRET_KEY;
  if (!secret) {
    console.error("RECAPTCHA_SECRET_KEY missing");
    return { ok: false, reason: "captcha-misconfigured" };
  }
  try {
    const res = await fetch("https://www.google.com/recaptcha/api/siteverify", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({ secret, response: token }).toString(),
    });
    const json = (await res.json()) as {
      success?: boolean;
      score?: number;
      action?: string;
      hostname?: string;
      "error-codes"?: string[];
    };
    if (!json.success) {
      console.warn("reCAPTCHA failed:", json["error-codes"]);
      return { ok: false, reason: "captcha-failed" };
    }
    if (json.action && json.action !== EXPECTED_ACTION) {
      console.warn("reCAPTCHA action mismatch:", json.action);
      return { ok: false, reason: "captcha-action-mismatch" };
    }
    if (typeof json.score === "number" && json.score < RECAPTCHA_MIN_SCORE) {
      console.warn("reCAPTCHA low score:", json.score);
      return { ok: false, reason: "captcha-low-score" };
    }
    return { ok: true };
  } catch (e) {
    console.error("reCAPTCHA verify error:", e);
    return { ok: false, reason: "captcha-error" };
  }
}


const GHL_WEBHOOK_URL =
  "https://services.leadconnectorhq.com/hooks/zrE8UrSIoY5k9UInJ6c7/webhook-trigger/202ac3fa-6447-4d97-b29b-b771a93ce0c1";

export const submitContactToGHL = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => input)
  .handler(async ({ data: rawData }) => {
    const parsed = schema.safeParse(rawData);
    if (!parsed.success) {
      const first = parsed.error.issues[0];
      const field = first?.path?.join(".") || "field";
      return { success: false, error: `Invalid ${field}: ${first?.message || "invalid value"}` };
    }
    const data = parsed.data;

    // 1. Honeypot — if a bot filled the hidden "website" field, silently
    // return success so the bot doesn't learn to bypass it.
    if (data.website && data.website.length > 0) {
      console.warn("Honeypot triggered — bot submission blocked");
      return { success: true };
    }

    // 2. Time-trap — reject submissions that happen faster than a human could fill the form.
    const elapsed = Date.now() - data.formLoadedAt;
    if (elapsed < MIN_FILL_TIME_MS) {
      console.warn("Form submitted too fast:", elapsed, "ms — bot suspected");
      return { success: true };
    }

    // 3. reCAPTCHA v3 verification with stricter score + action check.
    const captcha = await verifyRecaptcha(data.captchaToken);
    if (!captcha.ok) {
      return { success: false, error: "Captcha verification failed. Please try again." };
    }

    const fullName = `${data.firstName} ${data.lastName}`.trim();
    const message = data.notes || "";
    const tags = [
      "website-contact-form",
      ...data.helpWith.map((s) => `interest:${s}`),
    ];

    const payload = {
      id: `contact_${Date.now()}`,
      firstName: data.firstName,
      lastName: data.lastName,
      name: fullName,
      email: data.email,
      phone: data.phone,
      companyName: data.companyName,
      message,
      source: "Website Contact Form",
      tags,
      helpWith: data.helpWith,
      notes: message,
      submittedAt: new Date().toISOString(),
    };

    try {
      const res = await fetch(GHL_WEBHOOK_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "x-webhook-secret": process.env.GHL_WEBHOOK_SECRET ?? "",
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        console.error("GHL webhook failed:", res.status, text);
        return { success: false, error: `Webhook failed (${res.status})` };
      }

      return { success: true };
    } catch (e: any) {
      console.error("GHL webhook error:", e);
      return { success: false, error: e?.message || "Unknown error" };
    }
  });
